//* claim line item
import { z } from 'zod';
import { DbJsonSchema } from '../../dbJsonSchemas.js';
import { CrewClaimResolutionMethodEnum, CrewClaimStatusCode, CrewClaimStatusDetailCode, } from '../../enums/crew.js';
import { enumToZodLiteralUnion, helperSchema } from '../../zodExtensions.js';
import { appliedCustomField, appliedCustomFieldCreate } from './customField.js';
import { storeInspectionGrade, storeInspectionGradeCreate, } from './inspection.js';
import { storeOrderLineItem } from './lineItem.js';
import { storeProductVariant } from './storeProduct.js';
// ----------  Claim Line Item Inspection  ----------
export const inspectionImage = z.object({
    id: z.number(),
    url: z.string(),
});
export const inspectionImageCreate = inspectionImage
    .omit({
    id: true,
    url: true,
})
    .merge(z.object({ filename: z.string() }));
export const claimLineItemInspection = z.object({
    id: z.number(),
    createdOn: z.string(),
    name: z.string(),
    comment: helperSchema.nullifiedString.optional(),
    images: z.array(inspectionImage),
    customFields: z.array(appliedCustomField),
    storeInspectionGrade: storeInspectionGrade.optional(),
});
export const claimLineItemInspectionCreate = claimLineItemInspection
    .omit({
    id: true,
    images: true,
    storeInspectionGrade: true,
    customFields: true,
    createdOn: true,
})
    .merge(z.object({
    storeInspectionGrade: storeInspectionGradeCreate.optional(),
    images: z.array(inspectionImageCreate).optional(),
    customFields: z.array(z.object({ id: z.number(), value: z.string() })),
}));
// ----------  Claim Line Item  ----------
export const { variantExchangeLineItemSchema: variantExchangeLineItem } = DbJsonSchema.ClaimLineItem;
export const variantExchangeLineItemCreate = variantExchangeLineItem.pick({
    idFromPlatform: true,
    quantity: true,
});
export const claimLineItem = z.object({
    id: z.number(),
    finalizedOn: z.string().nullable(),
    canceledOn: z.string().optional(),
    deniedOn: z.string().optional(),
    returnLineItemIdFromPlatform: z.string().nullable(),
    suggestedWarrantyReplacementItem: storeProductVariant.optional(),
    // ! all line item claims
    noteToCustomer: z.string().optional(),
    errors: z.array(z.string()).optional(),
    requestedResolutionMethodEnum: enumToZodLiteralUnion(CrewClaimResolutionMethodEnum),
    resolutionMethodEnum: enumToZodLiteralUnion(CrewClaimResolutionMethodEnum).optional(),
    inspections: z.array(claimLineItemInspection),
    images: z.array(z.string()).optional(),
    comments: z.string().nullable(), // ? noteFromCustomer
    originalStoreOrderLineItemId: z.number(),
    originalStoreOrderLineItem: storeOrderLineItem,
    status: z.object({
        id: z.number(),
        name: z.string(),
        code: enumToZodLiteralUnion(CrewClaimStatusCode),
        detail: z.object({
            id: z.number(),
            name: z.string(),
            code: enumToZodLiteralUnion(CrewClaimStatusDetailCode),
        }),
    }),
    customFields: z.array(appliedCustomField),
    reason: z.object({
        id: z.number(),
        name: z.string(),
        detail: z
            .object({
            id: z.number(),
            name: z.string(),
        })
            .optional(), // ! detail will always remain optional on the reason as it's not required
    }),
    updatedOn: z.string().optional(),
    createdOn: z.string(),
    // ! Variant Exchange Line Item Claims
    variantExchangeLineItem: variantExchangeLineItem.optional(),
    replacementItems: z.array(variantExchangeLineItem).optional(),
    // ! Monetary Claims (Refund, Gift Card)
    quantity: z.number(),
    unitPrice: z.number(),
    unitTax: z.number(),
    amount: z.number().optional(), // issued monetary value of the claim line item applies to refunds and gift cards
    requestedAmount: z.number().optional(), // requested monetary value of the claim line item applies to refunds and gift cards
});
export const claimLineItemCreate = claimLineItem
    .omit({
    updatedOn: true,
    finalizedOn: true,
    createdOn: true,
    id: true,
    status: true,
    originalStoreOrderLineItem: true,
    inspections: true,
    resolutionMethodEnum: true,
    noteToCustomer: true,
    amount: true,
    returnLineItemIdFromPlatform: true,
    variantExchangeLineItem: true,
})
    .merge(z.object({
    variantExchangeLineItem: variantExchangeLineItemCreate.optional(),
    customFields: z.array(appliedCustomFieldCreate).optional(),
}));
const exchangeUpdate = z.object({
    variantExchangeLineItem: variantExchangeLineItemCreate,
    resolutionMethod: z.literal(`${CrewClaimResolutionMethodEnum.variantExchange}`),
});
const monetaryUpdate = z.object({ amount: z.number() });
const refundUpdate = z
    .object({
    resolutionMethod: z.literal(`${CrewClaimResolutionMethodEnum.refund}`),
})
    .merge(monetaryUpdate);
const giftCardUpdate = z
    .object({
    resolutionMethod: z.literal(`${CrewClaimResolutionMethodEnum.giftCard}`),
})
    .merge(monetaryUpdate);
const replacementOrderUpdate = z
    .object({
    resolutionMethod: z.literal(`${CrewClaimResolutionMethodEnum.replacementOrder}`),
})
    .merge(DbJsonSchema.ClaimLineItem.replacementOrderLineItems);
const claimLineItemUpdateApproved = z.discriminatedUnion('resolutionMethod', [
    exchangeUpdate,
    refundUpdate,
    giftCardUpdate,
    replacementOrderUpdate,
]);
const claimLineItemUpdateDeny = z.object({
    // ? these values come from the claim status helper... should we use the enum here?
    claimStatusDetailCode: z.union([
        z.literal(`${CrewClaimStatusDetailCode.wrongItem}`),
        z.literal(`${CrewClaimStatusDetailCode.itemNotReturned}`),
        z.literal(`${CrewClaimStatusDetailCode.noncompliantReturn}`),
        z.literal(`${CrewClaimStatusDetailCode.notCoveredUnderWarranty}`),
        z.literal(`${CrewClaimStatusDetailCode.noActionTaken}`),
    ]),
});
const claimLineItemUpdateCancel = z.object({
    // ? these values come from the claim status helper... should we use the enum here?
    claimStatusDetailCode: z.literal(`${CrewClaimStatusDetailCode.noActionTaken}`),
});
export const claimLineItemUpdate = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('note'),
        noteToCustomer: helperSchema.nullifiedString,
    }),
    z.object({
        kind: z.literal('approved'),
        approval: claimLineItemUpdateApproved,
    }),
    z.object({ kind: z.literal('denied'), denial: claimLineItemUpdateDeny }),
    z.object({
        kind: z.literal('cancelled'),
        cancellation: claimLineItemUpdateCancel,
    }),
]);
//# sourceMappingURL=claimLineItem.js.map